// Spec 48 ai-ux — chat_bubble_user / _assistant / _tool / _system.
//
// Four bubble variants under one component with `variant` discriminator:
//
//   user     — right-aligned, accent_subtle_bg, avatar at right.
//              role='article' aria-label='Your message sent at {timestamp}'.
//   assistant— left-aligned, bg_raised, product mark at top-left,
//              streaming cursor + citation footer + confidence + show-
//              query slots. role='article'. Streaming announced via
//              aria-live='polite' on content (debounced 500ms upstream).
//   tool     — inline neutral bg_subtle, smaller. <details>/<summary>
//              for "View call" disclosure of args + result.
//              role='article' aria-label='Tool call: {tool_name}'.
//   system   — muted inline single-line. role='status' aria-live='polite'.
//
// Right-alignment respects RTL — in RTL locales user bubble flips to
// left-align (CSS owns this via :dir() or [dir=rtl] attr; component
// emits data-variant for CSS targeting).

import { forwardRef, useState, type HTMLAttributes, type ReactNode } from "react";

import { cx } from "../lib/cx.ts";

export type ChatBubbleVariant = "user" | "assistant" | "tool" | "system";

// ==========================================================================
// USER VARIANT
// ==========================================================================

export interface ChatBubbleUserProps extends Omit<HTMLAttributes<HTMLElement>, "content"> {
  readonly variant: "user";
  /** Message body — ReactNode (HTMLAttributes ships its own string `content` attr we omit above). */
  readonly content: ReactNode;
  /** Pre-formatted timestamp string for the aria-label + tooltip. */
  readonly timestamp: string;
  readonly avatar?: ReactNode;
  readonly attachments?: ReactNode;
  /** Show "edited" chip when message was revised. */
  readonly edited?: boolean;
  readonly editedLabel?: string;
}

// ==========================================================================
// ASSISTANT VARIANT
// ==========================================================================

export interface ChatBubbleAssistantProps extends Omit<HTMLAttributes<HTMLElement>, "content"> {
  readonly variant: "assistant";
  /** Streaming or final message content — ReactNode (overrides HTMLAttributes string `content`). */
  readonly content: ReactNode;
  readonly timestamp: string;
  /** Slot for streaming cursor — typically <StreamingCursor> appended to content tail. */
  readonly streamingCursor?: ReactNode;
  /** Slot for citation chip footer. */
  readonly citationFooter?: ReactNode;
  /** Slot for confidence badge. */
  readonly confidenceBadge?: ReactNode;
  /** Slot for show-query disclosure trigger. */
  readonly showQueryToggle?: ReactNode;
  /** Slot for thumbs-up/down + copy + regenerate row. */
  readonly feedbackActions?: ReactNode;
  /** When true, content region declares aria-live='polite' for streaming announcement. */
  readonly isStreaming?: boolean;
}

// ==========================================================================
// TOOL VARIANT
// ==========================================================================

export interface ChatBubbleToolProps extends HTMLAttributes<HTMLElement> {
  readonly variant: "tool";
  readonly toolName: string;
  /** Human one-line summary, e.g., "Counted open reqs by department". */
  readonly toolSummary: string;
  /** Args/result content rendered inside the disclosure. */
  readonly callDetails?: ReactNode;
  readonly durationText?: string;
  readonly viewCallLabel?: string;
}

// ==========================================================================
// SYSTEM VARIANT
// ==========================================================================

export interface ChatBubbleSystemProps extends HTMLAttributes<HTMLElement> {
  readonly variant: "system";
  readonly eventText: string;
  readonly timestamp?: string;
  readonly icon?: ReactNode;
}

// ==========================================================================
// DISCRIMINATED-UNION ROOT
// ==========================================================================

export type ChatBubbleProps =
  | ChatBubbleUserProps
  | ChatBubbleAssistantProps
  | ChatBubbleToolProps
  | ChatBubbleSystemProps;

export const ChatBubble = forwardRef<HTMLElement, ChatBubbleProps>((props, ref) => {
  if (props.variant === "user") return <ChatBubbleUser {...props} ref={ref} />;
  if (props.variant === "assistant") return <ChatBubbleAssistant {...props} ref={ref} />;
  if (props.variant === "tool") return <ChatBubbleTool {...props} ref={ref} />;
  return <ChatBubbleSystem {...props} ref={ref} />;
});
ChatBubble.displayName = "ChatBubble";

// ==========================================================================
// IMPLEMENTATIONS
// ==========================================================================

const ChatBubbleUser = forwardRef<HTMLElement, ChatBubbleUserProps>(
  (
    {
      content,
      timestamp,
      avatar,
      attachments,
      edited,
      editedLabel = "edited",
      className,
      variant: _variant,
      ...rest
    },
    ref,
  ) => (
    <article
      ref={ref}
      data-component="chat-bubble"
      data-variant="user"
      aria-label={`Your message sent at ${timestamp}`}
      className={cx("nai-chat-bubble", "nai-chat-bubble--user", className)}
      {...rest as HTMLAttributes<HTMLElement>}
    >
      <div data-component="chat-bubble-content" className="nai-chat-bubble-content">
        {content}
      </div>
      {attachments !== undefined && (
        <div data-component="chat-bubble-attachments" className="nai-chat-bubble-attachments">
          {attachments}
        </div>
      )}
      <div className="nai-chat-bubble-meta">
        {edited === true && (
          <span data-component="chat-bubble-edited" className="nai-chat-bubble-edited">
            {editedLabel}
          </span>
        )}
        <span data-component="chat-bubble-timestamp" className="nai-chat-bubble-timestamp">
          {timestamp}
        </span>
      </div>
      {avatar !== undefined && (
        <span aria-hidden="true" data-component="chat-bubble-avatar" className="nai-chat-bubble-avatar">
          {avatar}
        </span>
      )}
    </article>
  ),
);
ChatBubbleUser.displayName = "ChatBubble.User";

const ChatBubbleAssistant = forwardRef<HTMLElement, ChatBubbleAssistantProps>(
  (
    {
      content,
      timestamp,
      streamingCursor,
      citationFooter,
      confidenceBadge,
      showQueryToggle,
      feedbackActions,
      isStreaming,
      className,
      variant: _variant,
      ...rest
    },
    ref,
  ) => (
    <article
      ref={ref}
      data-component="chat-bubble"
      data-variant="assistant"
      aria-label={`Assistant message from ${timestamp}`}
      className={cx("nai-chat-bubble", "nai-chat-bubble--assistant", className)}
      {...rest as HTMLAttributes<HTMLElement>}
    >
      <div
        data-component="chat-bubble-content"
        className="nai-chat-bubble-content"
        // Streaming announcement: polite live-region on content. Caller
        // debounces token append to ~500ms upstream so the screen reader
        // doesn't get spam.
        aria-live={isStreaming === true ? "polite" : undefined}
      >
        {content}
        {streamingCursor}
      </div>
      {citationFooter !== undefined && (
        <div data-component="chat-bubble-citations" className="nai-chat-bubble-citations">
          {citationFooter}
        </div>
      )}
      <div className="nai-chat-bubble-meta">
        {confidenceBadge}
        {showQueryToggle}
        {feedbackActions}
        <span data-component="chat-bubble-timestamp" className="nai-chat-bubble-timestamp">
          {timestamp}
        </span>
      </div>
    </article>
  ),
);
ChatBubbleAssistant.displayName = "ChatBubble.Assistant";

const ChatBubbleTool = forwardRef<HTMLElement, ChatBubbleToolProps>(
  (
    {
      toolName,
      toolSummary,
      callDetails,
      durationText,
      viewCallLabel = "View call",
      className,
      variant: _variant,
      ...rest
    },
    ref,
  ) => {
    const [open, setOpen] = useState(false);
    return (
      <article
        ref={ref}
        data-component="chat-bubble"
        data-variant="tool"
        aria-label={`Tool call: ${toolName}`}
        className={cx("nai-chat-bubble", "nai-chat-bubble--tool", className)}
        {...rest as HTMLAttributes<HTMLElement>}
      >
        <div className="nai-chat-bubble-tool-row">
          <span data-component="chat-bubble-tool-name" className="nai-chat-bubble-tool-name">
            {toolName}
          </span>
          <span data-component="chat-bubble-tool-summary" className="nai-chat-bubble-tool-summary">
            {toolSummary}
          </span>
          {durationText !== undefined && (
            <span
              data-component="chat-bubble-tool-duration"
              className="nai-chat-bubble-tool-duration"
            >
              {durationText}
            </span>
          )}
        </div>
        {callDetails !== undefined && (
          <>
            <button
              type="button"
              data-component="chat-bubble-tool-view-toggle"
              className="nai-chat-bubble-tool-view-toggle"
              aria-expanded={open}
              onClick={() => setOpen((o) => !o)}
            >
              {viewCallLabel}
            </button>
            {open && (
              <div
                data-component="chat-bubble-tool-call-details"
                className="nai-chat-bubble-tool-call-details"
                aria-label="Tool call payload"
              >
                {callDetails}
              </div>
            )}
          </>
        )}
      </article>
    );
  },
);
ChatBubbleTool.displayName = "ChatBubble.Tool";

// Keep HTMLElement as the ref type so the discriminated-union root
// `forwardRef<HTMLElement, ChatBubbleProps>` can forward into any variant
// uniformly. The system variant renders a <div>, so we cast the ref at
// the element boundary.
const ChatBubbleSystem = forwardRef<HTMLElement, ChatBubbleSystemProps>(
  ({ eventText, timestamp, icon, className, variant: _variant, ...rest }, ref) => (
    // System bubble uses <div role="status">, NOT <article role="status">.
    // Per WAI-ARIA + axe-core's aria-allowed-role rule, the `article`
    // element's default role is "article" and does not permit a `status`
    // override — axe flags <article role="status"> as a violation. User /
    // assistant / tool variants keep <article> because their aria-label=
    // "…message…" semantics map correctly to the article role; the system
    // variant is a live-region announcement, not a message, so div+status
    // is the WAI-ARIA-correct pairing per spec 48 line 634.
    <div
      ref={ref as React.Ref<HTMLDivElement>}
      role="status"
      aria-live="polite"
      data-component="chat-bubble"
      data-variant="system"
      className={cx("nai-chat-bubble", "nai-chat-bubble--system", className)}
      {...rest as HTMLAttributes<HTMLDivElement>}
    >
      {icon !== undefined && (
        <span aria-hidden="true" data-component="chat-bubble-system-icon" className="nai-chat-bubble-system-icon">
          {icon}
        </span>
      )}
      <span data-component="chat-bubble-system-text" className="nai-chat-bubble-system-text">
        {eventText}
      </span>
      {timestamp !== undefined && (
        <span data-component="chat-bubble-timestamp" className="nai-chat-bubble-timestamp">
          {timestamp}
        </span>
      )}
    </div>
  ),
);
ChatBubbleSystem.displayName = "ChatBubble.System";
